import React, { useState } from 'react';
import { getDefaultCurrency } from './conversions';
import { Typography, Box, Table, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import FeaturesTable from './components/FeaturesTable';
import GetInTouchButton from './components/GetInTouchButton';
import CurrencyToggle from './components/CurrencyToggle';
import PricingCard from './components/PricingCard';
import TableHeaderCell from './components/TableHeaderCell';
import FeaturesList from './components/FeaturesList';
import { usePricing } from './hooks/usePricing';
import { LAYOUT_CONSTANTS } from './constants';

const Pricing = () => {
  const [numComplianceReports, setNumComplianceReports] = useState(3);
  const [employeeSizeStep, setEmployeeSizeStep] = useState(3);
  const [currency, setCurrency] = useState(getDefaultCurrency());

  const { pricing, pricePerReport, totalPrice, currentEmployeePrice, currencies } = usePricing(
    currency,
    numComplianceReports,
    employeeSizeStep,
  );

  const handleCurrencyChange = (event, newCurrency) => {
    if (newCurrency !== null) {
      setCurrency(newCurrency.toLowerCase());
    }
  };

  return (
    <Box sx={{ width: '100vw', bgcolor: 'white' }}>
      <Box
        sx={{
          maxWidth: '1120px',
          mx: 'auto',
          pt: 16,
          pb: 10,
          px: 2,
        }}
      >
        <Box
          id="founding-partner-program"
          className="alert-border-wrapper"
          sx={{
            borderRadius: 2,
            p: '1.5px',
            mb: 5,
            maxWidth: 760,
            mx: 'auto',
            '--alert-bg': '#eef2ff',
            '--alert-shadow': '0 10px 20px -16px rgba(67, 56, 202, 0.6)',
          }}
        >
          <Box
            className="alert-border-content"
            sx={{
              color: '#312e81',
              borderRadius: 2,
              px: 3,
              py: 2,
              textAlign: 'left',
            }}
          >
            <Box
              sx={{
                fontWeight: 700,
                fontSize: '1.5em',
                color: '#4338ca',
                letterSpacing: '0.03em',
                mb: 0.5,
                textAlign: 'center',
              }}
            >
              2026 LIMITED OFFER: Founding Partner Program
            </Box>
            The Founding Partner Program is designed for companies leading the shift to AI-native pay-equity strategy.
            We’re offering a limited number of organizations private access to Sophare on their own data, reserved for
            teams ready to move early. Please{' '}
            <a href="/contact" style={{ fontWeight: '700', color: '#4f46e5' }}>
              contact us
            </a>{' '}
            to discuss your eligibility to participate and secure your place.
          </Box>
        </Box>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2.5rem', sm: '3.75rem' },
            fontWeight: 'bold',
            textAlign: 'center',
            mb: 6,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          Transparency Is Our Business
          <br />
          <span style={{ fontWeight: 500 }}>That Includes Pricing</span>
        </Typography>

        <FeaturesList />

        <CurrencyToggle currency={currency} onCurrencyChange={handleCurrencyChange} currencies={currencies} />

        {/* Pricing sliders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6 items-stretch">
          <PricingCard
            title="Compliance Reporting"
            subtitle={`Number of reports: ${numComplianceReports}`}
            value={numComplianceReports}
            onChange={(_, value) => setNumComplianceReports(Number(value))}
            marks={pricing.complianceReporting}
            price={pricePerReport}
            totalPrice={totalPrice}
            currency={currency}
            currencies={currencies}
            theme="light"
            showUnit={true}
          />

          <PricingCard
            title="Global Pay Equity Modeling"
            subtitle={`Employee size: ${pricing.globalPayEquityModeling[employeeSizeStep - 1].longLabel}`}
            value={employeeSizeStep}
            onChange={(_, value) => setEmployeeSizeStep(Number(value))}
            marks={pricing.globalPayEquityModeling}
            price={currentEmployeePrice}
            currency={currency}
            currencies={currencies}
            theme="dark"
            isPopular={true}
            showCustom={true}
            showUnit={false}
          />
        </div>

        {/* Comparison table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            overflow: 'visible', // allow sticky header to render outside clipping
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          }}
        >
          {/* stickyHeader helps with proper CSS classes; we also set explicit sx for viewport stickiness */}
          <Table stickyHeader>
            <colgroup>
              <col style={{ width: '38%' }} />
              <col style={{ width: '31%' }} />
              <col style={{ width: '31%' }} />
            </colgroup>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#f8fafc',
                backdropFilter: 'saturate(180%) blur(6px)',
                boxShadow: 'inset 0 -1px 0 #e2e8f0',
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 11,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.8rem', md: '1.1rem' },
                    borderBottom: '2px solid #e2e8f0',
                    backgroundColor: 'inherit',
                    verticalAlign: { xs: 'top', md: 'middle' },
                    pr: { xs: 0, md: 2 },
                    pl: { xs: 1, md: 2 },
                  }}
                >
                  Plan
                </TableCell>

                <TableHeaderCell
                  title="Compliance Reporting"
                  price={pricePerReport}
                  currency={currency}
                  currencies={currencies}
                  theme="light"
                  sx={{ pr: { xs: 1, md: 2 } }}
                />

                <TableHeaderCell
                  title="Global Pay Equity Modeling"
                  price={currentEmployeePrice}
                  currency={currency}
                  currencies={currencies}
                  theme="dark"
                  hideUnit={true}
                  showCustom={true}
                  sx={{ pl: { xs: 1, md: 2 } }}
                />
              </TableRow>
            </TableHead>
            <FeaturesTable />
          </Table>
        </TableContainer>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          {GetInTouchButton(
            'large',
            {
              px: { xs: 6, md: 8 },
              minWidth: { xs: 220, md: 280 },
            },
            'dark',
          )}
        </Box>
      </Box>

      {/* enforce minimum height on cards via MUI breakpoint-friendly sx */}
      <style>{`
        /* Use CSS because tailwind classes are already present; this ensures min-height applies consistently */
        @media (min-width: 0px) {
          .rounded-2xl.border.bg-white.p-6.md\\:p-8.flex.flex-col.h-full { min-height: ${LAYOUT_CONSTANTS.CARD_MIN_HEIGHT.xs}px; }
        }
        @media (min-width: 900px) {
          .rounded-2xl.border.bg-white.p-6.md\\:p-8.flex.flex-col.h-full { min-height: ${LAYOUT_CONSTANTS.CARD_MIN_HEIGHT.md}px; }
        }
      `}</style>
    </Box>
  );
};

export default Pricing;
